import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Card, Collapse, Popconfirm, Tag, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { isStaffEmail } from "@utils/user_utils";
import { PremiumFeatureDisabled } from "@components/Common/PremiumFeatureDisabled";
import { AdminNotice } from "@components/Settings/AdminNotice";
import { PermissionedButton as PermissionedButtonBase } from "@components/PermissionedComponents";
const PermissionedButton = PermissionedButtonBase as unknown as React.FC<any>;
import {
    createUserDefinedFlag,
    deleteUserDefinedFlag,
    getUserDefinedFlags,
    updateUserDefinedFlag,
} from "@store/actions/userDefinedFlags.actions";
import { UserDefinedFlag } from "@store/reducers/userDefinedFlags.reducer";
import { buildMetricTreeData } from "./utils";
import { FLAG_TYPE_COACH, FLAG_TYPE_LOGIC } from "./constants";
import { VariableOption } from "./types";
import FlagForm from "./FlagForm";
import CopyFlagButton from "./CopyFlagButton";
import BlurredUserDefinedFlags from "./blurred_user_defined_flags.png";
import "@styles/styles-UserDefinedFlags.css";

const { Text } = Typography;

const emptyFlag = (): Omit<UserDefinedFlag, "id"> => ({
    name: "",
    abbreviation: "",
    description: "",
    color: "#1890ff",
    is_active: true,
    type: FLAG_TYPE_LOGIC,
    config: { condition: "", fetch_params: {} },
});

export const UserDefinedFlags: React.FC = () => {
    const dispatch = useDispatch<any>();

    const { flags, loading } = useSelector(({ userDefinedFlags }: any) => userDefinedFlags);
    const { all_metric_types, misc, allPlayers, positionEntities, playerGroups, info } = useSelector(
        ({ team }: any) => team
    );
    const user_defined_flags_enabled: boolean = info?.user_defined_flags_enabled ?? false;

    const { user } = useAuth0();
    const isStaff = isStaffEmail(user?.email);

    const variableOptions: VariableOption[] = misc?.user_defined_flag_vars ?? [];
    const metricTreeData = useMemo(() => buildMetricTreeData(all_metric_types), [all_metric_types]);

    const [creating, setCreating] = useState(false);
    const [expandedId, setExpandedId] = useState<number | null | string>(null);
    const [saving, setSaving] = useState(false);
    const [draftFlags, setDraftFlags] = useState<(UserDefinedFlag & { _tempId: string })[]>([]);

    useEffect(() => {
        if (!user_defined_flags_enabled && !isStaff) return;
        dispatch(getUserDefinedFlags());
    }, [dispatch, user_defined_flags_enabled, isStaff]);

    const handleDuplicateFlag = (flag: UserDefinedFlag) => {
        const clonedFlag: UserDefinedFlag & { _tempId: string } = {
            ...flag,
            name: `${flag.name} - Copy`,
            _tempId: `temp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
            id: undefined,
        };
        setDraftFlags((prev) => [...prev, clonedFlag]);
        setExpandedId(clonedFlag._tempId);
    };

    const handleSaveDraft = async (draftId: string, data: Omit<UserDefinedFlag, "id">) => {
        setSaving(true);
        const success = await dispatch(createUserDefinedFlag(data));
        setSaving(false);
        if (success) {
            setDraftFlags((prev) => prev.filter((d) => d._tempId !== draftId));
            setExpandedId(null);
        }
    };

    const handleCancelDraft = (draftId: string) => {
        setDraftFlags((prev) => prev.filter((d) => d._tempId !== draftId));
        setExpandedId(null);
    };

    const handleCreate = async (data: Omit<UserDefinedFlag, "id">) => {
        setSaving(true);
        const success = await dispatch(createUserDefinedFlag(data));
        setSaving(false);
        if (success) setCreating(false);
    };

    const handleUpdate = async (id: number, data: Omit<UserDefinedFlag, "id">) => {
        setSaving(true);
        await dispatch(updateUserDefinedFlag(id, data));
        setSaving(false);
    };

    const handleDelete = async (id: number) => {
        await dispatch(deleteUserDefinedFlag(id));
        if (expandedId === id) setExpandedId(null);
    };

    const sharedFormProps = {
        variableOptions,
        metricTreeData,
        players: allPlayers ?? [],
        positionEntities: positionEntities ?? [],
        playerGroups: playerGroups ?? [],
        saving,
    };

    if (!user_defined_flags_enabled && !isStaff) {
        return (
            <PremiumFeatureDisabled
                bg_image={BlurredUserDefinedFlags}
                show_navbar={false}
                feature_name={"User Defined Flags"}
                info_text={
                    <>
                        This is a premium feature that lets you create custom flag conditions based on your team's
                        GPS, wellness, and other metrics. <br />
                        If you are interested in adding this to your platform, please contact your sales rep!
                    </>
                }
            />
        );
    }

    return (
        <div className="udf-container">
            {!user_defined_flags_enabled && isStaff && <AdminNotice style={{marginBottom: 20}} />}
            <div className="udf-header">
                <PermissionedButton
                    permissionCheck="ManageCustomFlagConfigurations"
                    permissionMessage="You don't have permission to manage user defined flags"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => { setCreating(true); setExpandedId(null); }}
                    disabled={creating}
                >
                    New Flag
                </PermissionedButton>
            </div>

            {creating && (
                <Card className="udf-card udf-card-new" size="small" title="New Flag">
                    <FlagForm
                        {...sharedFormProps}
                        initial={emptyFlag()}
                        onSave={handleCreate}
                        onCancel={() => setCreating(false)}
                    />
                </Card>
            )}

            {loading && flags.length === 0 && <div className="udf-empty">Loading...</div>}

            {!loading && flags.length === 0 && !creating && (
                <div className="udf-empty">
                    No user defined flags yet. Click <strong>New Flag</strong> to create one.
                </div>
            )}

            {flags.map((flag: UserDefinedFlag) => (
                <Collapse
                    key={flag.id}
                    className="udf-card"
                    activeKey={expandedId === flag.id ? [flag.id!] : []}
                    onChange={(keys) => {
                        if (keys.length > 0) {
                            setExpandedId(flag.id!);
                            setCreating(false);
                        } else {
                            setExpandedId(null);
                        }
                    }}
                >
                    <Collapse.Panel
                        key={flag.id!}
                        header={
                            <div className="udf-card-title">
                                <span className="udf-color-dot" style={{ backgroundColor: flag.color }} />
                                <Text strong>{flag.name}</Text>
                                <Tag color="default" className="udf-abbr-tag">{flag.abbreviation}</Tag>
                                <Tag color={flag.type === FLAG_TYPE_COACH ? "purple" : "blue"}>
                                    {flag.type === FLAG_TYPE_COACH ? "Coach" : "Logic"}
                                </Tag>
                                <Tag color={flag.is_active ? "green" : "default"}>
                                    {flag.is_active ? "Active" : "Inactive"}
                                </Tag>
                            </div>
                        }
                        extra={
                            <div style={{ display: "flex", gap: "4px" }}>
                                <CopyFlagButton flag={flag} onDuplicate={handleDuplicateFlag} />
                                <Popconfirm
                                    title="Delete flag"
                                    description={`Are you sure you want to delete "${flag.name}"?`}
                                    onConfirm={(e) => { e?.stopPropagation(); handleDelete(flag.id!); }}
                                    onCancel={(e) => e?.stopPropagation()}
                                    okText="Delete"
                                    okButtonProps={{ danger: true }}
                                    cancelText="Cancel"
                                >
                                    <PermissionedButton
                                        permissionCheck="ManageCustomFlagConfigurations"
                                        permissionMessage="You don't have permission to manage user defined flags"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    />
                                </Popconfirm>
                            </div>
                        }
                    >
                        <FlagForm
                            {...sharedFormProps}
                            initial={flag}
                            isExpanded={expandedId === flag.id}
                            onSave={(data) => handleUpdate(flag.id!, data)}
                            onCancel={() => setExpandedId(null)}
                        />
                    </Collapse.Panel>
                </Collapse>
            ))}

            {draftFlags.map((draft) => (
                <Card
                    key={draft._tempId}
                    className="udf-card udf-card-new"
                    size="small"
                    title={
                        <div className="udf-card-title">
                            <span className="udf-color-dot" style={{ backgroundColor: draft.color }} />
                            <Text strong>{draft.name}</Text>
                            <Tag color="default" className="udf-abbr-tag">{draft.abbreviation}</Tag>
                            <Badge status="processing" text="Unsaved" style={{ marginLeft: "8px" }} />
                        </div>
                    }
                >
                    <FlagForm
                        {...sharedFormProps}
                        initial={draft}
                        isExpanded={expandedId === draft._tempId}
                        onSave={(data) => handleSaveDraft(draft._tempId, data)}
                        onCancel={() => handleCancelDraft(draft._tempId)}
                    />
                </Card>
            ))}
        </div>
    );
};

export default UserDefinedFlags;
